import { css, getResizeObserverPolyfilled, Icon } from "@sourceful/shared-components";
import { FC, useEffect, useRef, useState, Children } from "react";
import toRem from "@styles/postprocess/conversion/to-rem";

const ResizeObserver = getResizeObserverPolyfilled();

const scrollContainerCss = css({
  overflow: "hidden",
  position: "relative",

  variants: {
    constrainContent: {
      yes: {},
      no: {
        marginInline: "clamp(-60px, -3vw, -20px)", // Inverted values, these are based on the BasePageSection padding rules
      },
    },
  },
});

const scrollButtons = css({
  display: "none",

  "@media (hover: hover) and (pointer: fine)": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "absolute",
    top: "50%",
    left: "$inline_m1",
    right: "$inline_m1",
    transform: "translateY(-50%)",
    zIndex: "1",
    height: "0",
    overflow: "visible",

    "@sm": {
      left: "calc($inline_m1 * 2)",
      right: "calc($inline_m1 * 2)",
    },
  },

  variants: {
    constrainContent: {
      yes: {},
      no: {},
    },
  },
});

const scrollCss = css({
  overflowX: "auto",
  whiteSpace: "nowrap",
  paddingBlockEnd: "$inline_m1",
  marginBlockEnd: "-$inline_s2",
  scrollSnapType: "x mandatory",

  "&::-webkit-scrollbar": {
    display: "none",
  },
});

const scrollInnerCss = css({
  width: "100%",
  display: "flex",
  flexDirection: "row",
  margin: "0",
  padding: "0",
});

const scrollItemCss = css({
  display: "flex",
  flexGrow: "0",
  flexShrink: "0",
  scrollSnapAlign: "center",
  marginInlineEnd: "calc($space$inline_m1 - 16px)",

  "> .carousel-card": {
    whiteSpace: "normal",
  },

  "@<sm": {
    "> .carousel-card": {
      width: "100%",
    },
  },

  "@sm": {
    flex: "none",

    "> .carousel-card": {
      flex: "auto",
    },
  },

  variants: {
    constrainContent: {
      yes: {
        "@<sm": {
          "> .carousel-card": {
            // Slightly smaller than below because we lose some width with the constraint container
            maxWidth: toRem(270),
          },
        },
      },
      no: {
        "@<sm": {
          "> .carousel-card": {
            maxWidth: toRem(280),
          },
        },

        "&:first-child": {
          paddingInlineStart: "calc($space$inline_m1 - 8px)",
        },

        "&:last-child": {
          marginInlineEnd: "0",
          paddingInlineEnd: "calc($space$inline_m1 - 8px)",
        },

        "@sm": {
          transform: "translateX(calc(max(1200px, 100vw - 48px)/2 - 600px))",

          "@media (min-width: 1200px)": {
            transform:
              "translateX(calc(max(1200px, 100vw - 48px - var(--scrollbar-width))/2 - 600px))",
          },
        },
      },
    },
  },
});

const scrollButtonCss = css({
  rad: "$round",
  backgroundColor: "$palette3_colour2",
  cursor: "pointer",
  variants: {
    left: {
      yes: {},
      no: {
        marginInlineStart: "auto",
      },
    },
  },
});

const srCss = css({
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: "0",
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  borderWidth: 0,
});

interface ResponsiveCarouselProps {
  itemWidth: number;
  gridGap?: number;
  numItems: number;
  constrainContent?: boolean;
  children?: React.ReactNode;
}

const ResponsiveCarousel: FC<ResponsiveCarouselProps> = ({ constrainContent, children }) => {
  const arrayChildren = Children.toArray(children);

  const [scrollLeftButtonVisible, setScrollLeftButtonVisible] = useState(false);
  const [scrollRightButtonVisible, setScrollRightButtonVisible] = useState(false);

  const componentElRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const setLeftButtonVisible = () => {
    if (scrollRef.current) {
      const hasScroll = scrollRef.current.scrollWidth > scrollRef.current.clientWidth;

      setScrollLeftButtonVisible(hasScroll && Math.round(scrollRef.current.scrollLeft) > 0);
    }
  };

  const setRightButtonVisible = () => {
    if (scrollRef.current) {
      const hasScroll = scrollRef.current.scrollWidth > scrollRef.current.clientWidth;

      // console.log(
      //   "setRightButtonVisible",
      //   scrollRef.current.scrollWidth,
      //   scrollRef.current.clientWidth,
      //   scrollRef.current.scrollLeft,
      //   hasScroll,
      //   Math.ceil(scrollRef.current.scrollLeft + scrollRef.current.clientWidth)
      // );

      setScrollRightButtonVisible(
        hasScroll &&
          Math.ceil(scrollRef.current.scrollLeft) + Math.ceil(scrollRef.current.clientWidth) <
            Math.ceil(scrollRef.current.scrollWidth)
      );
    }
  };

  const getScrollAmount = () => {
    return Number.parseInt(scrollRef.current?.getAttribute("data-scroll-amount") || "0");
  };

  const scrollLeftClick = () => {
    //console.log("ResponseiveCarousel.scrollLeftClick", scrollRef);

    if (scrollRef.current) {
      const left = Math.round(scrollRef.current.scrollLeft) - getScrollAmount();

      scrollRef.current.scroll({
        left: left,
        behavior: "smooth",
      });
    }
  };

  const scrollRightClick = () => {
    //console.log("ResponseiveCarousel.scrollRightClick", scrollRef, getScrollAmount());

    if (scrollRef.current) {
      const left = Math.round(scrollRef.current.scrollLeft) + getScrollAmount();

      scrollRef.current?.scroll({
        left: left,
        behavior: "smooth",
      });
    }
  };

  const resetListItemTabIndexes = () => {
    const cardsElList = document.querySelectorAll("[data-list-item] a");

    cardsElList.forEach(el => {
      (el as HTMLAnchorElement).tabIndex = 0;
    });
  };

  const cardObserver = (entries: IntersectionObserverEntry[]) => {
    //console.log("cardObserver", entries, observer);

    entries.forEach(function (entry) {
      const cardEl = entry,
        cardElLink = cardEl?.target.querySelector("a") as HTMLAnchorElement;

      if (cardElLink) {
        cardElLink.tabIndex = cardEl.intersectionRatio === 1 ? 0 : -1;
      }
    });
  };

  function setScrollAmount() {
    const firstEl = componentElRef.current?.querySelector("[data-list-item]");

    if (firstEl) {
      scrollRef.current?.setAttribute("data-scroll-amount", firstEl.clientWidth.toString());
    }
  }

  const scrollResizeObserver = () => {
    setScrollAmount();

    setLeftButtonVisible();
    setRightButtonVisible();
  };

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--scrollbar-width",
      window.innerWidth - document.documentElement.clientWidth + "px"
    );

    const cardsElList = componentElRef.current?.querySelectorAll("[data-list-item]"),
      intersectionObserver = new IntersectionObserver(cardObserver, { threshold: 1 }),
      resizeObserver = new ResizeObserver(scrollResizeObserver);

    resizeObserver.observe(scrollRef.current as Element);

    cardsElList?.forEach(el => intersectionObserver.observe(el));

    //console.log("setting scrollAmount", cardsElList[0].clientWidth);

    // Assuming everything is the same width, we could make this a lot more dynamic
    setScrollAmount();

    setLeftButtonVisible();
    setRightButtonVisible();

    return () => {
      if (scrollRef.current) {
        resizeObserver.unobserve(scrollRef.current as Element);
      }
      cardsElList?.forEach(el => intersectionObserver.unobserve(el));
    };
  }, []);

  return (
    <div
      ref={componentElRef}
      className={scrollContainerCss({
        constrainContent: constrainContent ? "yes" : "no",
      })}
    >
      {/* NOTE: Its basically a list we're horizontal scrolling so lets keep it semantic. 
              A true carousel would be different based on accessibility guidelines.
              Our buttons are to help scroll as we've hidden the scrollbar. */}
      <div
        className={scrollButtons({
          constrainContent: constrainContent ? "yes" : "no",
        })}
      >
        {scrollLeftButtonVisible && (
          <button
            type="button"
            onClick={scrollLeftClick}
            className={scrollButtonCss({ left: "yes" })}
          >
            <span className={srCss()}>Scroll left</span>
            <Icon name="arrow-direction-left" />
          </button>
        )}
        {scrollRightButtonVisible && (
          <button
            type="button"
            onClick={scrollRightClick}
            className={scrollButtonCss({ left: "no" })}
          >
            <span className={srCss()}>Scroll right</span>
            <Icon name="arrow-direction-right" />
          </button>
        )}
      </div>
      <div
        ref={scrollRef}
        className={scrollCss()}
        onFocus={() => {
          resetListItemTabIndexes();
        }}
        onScroll={() => {
          setLeftButtonVisible();
          setRightButtonVisible();
        }}
      >
        <ul className={scrollInnerCss()} data-list>
          {Children.map(arrayChildren, (child, index) => {
            return (
              <li
                key={`rc-${index}`}
                className={scrollItemCss({
                  constrainContent: constrainContent ? "yes" : "no",
                })}
                data-list-item
              >
                {child}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default ResponsiveCarousel;
